import type { Session, SupabaseClient } from '@supabase/supabase-js';
import { Logger } from '../logger';
import { createClient } from './client';

const TOKEN_REFRESH_BUFFER_SECONDS = 30;

export function isSessionUsable(session: Session | null): session is Session {
  if (!session?.access_token) {
    return false;
  }

  if (!session.expires_at) {
    return true;
  }

  const nowSeconds = Math.floor(Date.now() / 1000);
  return session.expires_at > nowSeconds + TOKEN_REFRESH_BUFFER_SECONDS;
}

export async function getBrowserSession(
  supabase: SupabaseClient = createClient()
): Promise<Session | null> {
  const { data, error } = await supabase.auth.getSession();

  if (error) {
    Logger.warn('Failed to get authentication session', { error: error.message });
  }

  const session = data.session ?? null;

  if (!session) {
    return null;
  }

  const usableSession = isSessionUsable(session) ? session : null;

  if (usableSession) {
    return usableSession;
  }

  if (!session.refresh_token) {
    return null;
  }

  const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();

  if (refreshError) {
    Logger.warn('Failed to refresh authentication session', { error: refreshError.message });
    return null;
  }

  return isSessionUsable(refreshed.session ?? null) ? refreshed.session : null;
}

export async function getBrowserAccessToken(): Promise<string | null> {
  if (typeof window === 'undefined') return null;

  try {
    const session = await getBrowserSession();
    return session?.access_token ?? null;
  } catch (error) {
    const err = error instanceof Error ? error.message : String(error);
    Logger.warn('Failed to get authentication session', { error: err });
    return null;
  }
}